import { useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { AirSettingsBusinessSource } from '@frontend/api-ai-settings';
import { useTranslation } from '@frontend/i18n';
import { TextField, Text } from '@frontend/design-system';
import { BusinessNameSourceValue } from '../../../../types/air-settings.types';
import { BulkOverrideWarningAlert } from '../../common/bulk-override-warning-alert';
import { ContextualDataAlert } from '../../common/contextual-data-alert';
import { KnowledgeSourceInput } from '../../common/knowledge-source-input';
import { BusinessNameSourceLabelMap, BusinessNameSourceLinkMap } from '../constants';
import { BusinessNameSourceFieldProps, BusinessNameCustomValueFieldProps } from './use-business-knowledge-form';

const DEFAULT_SOURCES: BusinessNameSourceValue[] = [
  { source: 'IAM', value: '' },
  { source: 'ORGANIZATION_SETTINGS', value: '' },
  { source: 'BRAND', value: '' },
];

type Props = {
  sourceFieldProps: BusinessNameSourceFieldProps;
  customValueFieldProps: BusinessNameCustomValueFieldProps;
  sourceList?: BusinessNameSourceValue[];
  isBulkEdit?: boolean;
  showBulkOverrideWarningAlert?: boolean;
};

export const BusinessNameInput = ({
  sourceFieldProps,
  customValueFieldProps,
  sourceList = DEFAULT_SOURCES,
  isBulkEdit = false,
  showBulkOverrideWarningAlert = false,
}: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const customFieldRef = useRef<HTMLDivElement>(null);
  const previousSource = useRef(sourceFieldProps.value);

  useEffect(() => {
    if (sourceFieldProps.value === 'CUSTOM' && previousSource.current !== 'CUSTOM') {
      const customInputElement = customFieldRef.current?.querySelector('input');
      customInputElement?.focus();
    }
    previousSource.current = sourceFieldProps.value;
  }, [sourceFieldProps.value]);

  const options = useMemo(() => {
    const listWithCustom = sourceList.some((s) => s.source === 'CUSTOM')
      ? sourceList
      : [...sourceList, { source: 'CUSTOM' } as BusinessNameSourceValue];

    return listWithCustom
      .filter(({ source, value }) => isBulkEdit || source === 'CUSTOM' || !!value)
      .map(({ source }) => ({
        value: source,
        label: BusinessNameSourceLabelMap[source],
        settingsPageLink: BusinessNameSourceLinkMap[source],
      }));
  }, [sourceList, isBulkEdit]);

  const renderSourceData = useCallback(
    (source: AirSettingsBusinessSource) => {
      if (!source) {
        return null;
      }
      if (source === 'CUSTOM') {
        return (
          <>
            <div css={styles.textFieldWrapper}>
              <TextField
                {...customValueFieldProps}
                ref={customFieldRef}
                placeholder={t('Enter business name')}
                label={t('Business Name')}
              />
            </div>
            {showBulkOverrideWarningAlert && <BulkOverrideWarningAlert />}
          </>
        );
      } else if (isBulkEdit) {
        return <ContextualDataAlert />;
      } else {
        return (
          <div css={styles.displayValueBox}>
            <Text size='medium'>{sourceList.find((ns) => ns.source === source)?.value}</Text>
          </div>
        );
      }
    },
    [isBulkEdit, customValueFieldProps, t, showBulkOverrideWarningAlert, sourceList]
  );

  return (
    <KnowledgeSourceInput<any, any, AirSettingsBusinessSource>
      fieldProps={sourceFieldProps as any}
      options={options}
      renderSourceData={renderSourceData}
    />
  );
};

const styles = {
  textFieldWrapper: css`
    padding: var(--fabric-space-padding-xs) var(--fabric-space-padding-xs);
    border-radius: var(--fabric-radius-xs);
    background-color: var(--fabric-color-surface-default);
  `,
  displayValueBox: css`
    padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-md);
    border-radius: var(--fabric-radius-sm);
    background-color: var(--fabric-color-surface-default);
  `,
};
